import { TabView, TabPanel } from "primereact/tabview";
import { GetModuleUsersApiResponseDto, MessageDto } from "../../general/dtos";
import { Api } from "../../general/api";
import { useContext, useEffect, useState } from "react";
import { GeneralErrorDialog } from "../dialogs/error-dialog";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { AxiosError } from "axios";
import { ModuleManagementDataContext, ModuleManagementDataContextValue } from "../../providers/module-management-provider";
import { ModuleDetails } from "./module-details";
import { ModuleAssignments } from "./module-assignments";
import { ModuleUsage } from './module-usage';
import { useApi } from "../../providers/api-provider";
import { useBookmarkTab } from '../../general/bookmark-selection';

interface ModuleManagementPageProps {
}

export default function ModuleManagementPage({ }: ModuleManagementPageProps) {
  const [activeTab, setActiveTab] = useBookmarkTab(['details', 'assignments', 'usage']);
  const ctx: ModuleManagementDataContextValue | null = useContext<ModuleManagementDataContextValue | null>(ModuleManagementDataContext);
  const { mode, allUsersError, moduleUsersError, selectedModuleError, moduleUsers, loadModuleUsers, setModuleUsersForModule, selectedModules } = ctx!;

  const api: Api = useApi();
  const [error, setError] = useState<string>('');
  const [errorTitle, setErrorTitle] = useState<string>('');
  const [errorDetails, setErrorDetails] = useState<string>('');

  useEffect(() => {
    if (selectedModules && selectedModules.length === 1 && !moduleUsers[selectedModules[0].module_key]) {
        loadModuleUsers(selectedModules[0]?.module_key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModules]);

  useEffect(() => {
    if (allUsersError) {
        setError(allUsersError);
        setErrorTitle('Error loading user list');
        setErrorDetails('There was an unhandled error while trying to load the user list. Please try again later or contact support :)');
    }
    if (moduleUsersError) {
        setError(moduleUsersError);
        setErrorTitle('Error loading module assignments');
        setErrorDetails('There was an unhandled error while trying to load module assignments. Please try again later or contact support :)');
    }
    if (selectedModuleError) {
        setError(selectedModuleError);
        setErrorTitle(mode === 'view' ? 'Unable to load module' : 'Unable to save module');
        setErrorDetails(mode === 'view' ? 'Try opening the module again. If the problem continues, contact support.'
            : 'Your changes are still in the editor. Review the message, make any corrections, and save again.');
    }
  }, [allUsersError, moduleUsersError, selectedModuleError, mode]);
  
  const saveModuleUsers = async (module_key: string, assigned: string[]): Promise<GetModuleUsersApiResponseDto | MessageDto | null> => {
    try {
        const json: GetModuleUsersApiResponseDto | MessageDto = await api.saveModuleUsers(module_key, assigned);
        if ('data' in json) {
            // keep the provider up to date
            setModuleUsersForModule(module_key, (json as GetModuleUsersApiResponseDto).data);
        }
        return json;
    } catch (error: AxiosError | any) {
        const message: string = error.response?.data?.message ?? 'Save error';
        setError(message);
        setErrorTitle('Error saving module assignments');
        setErrorDetails('There was an unhandled error while trying to save module assignments. Please try again later or contact support :)');
    }
    return null;
  }

  return (<>
    <TabView activeIndex={activeTab} onTabChange={event => setActiveTab(event.index)}
        className={`module-management-tabs flex flex-column h-full${activeTab === 0 && (mode === 'edit' || mode === 'create') ? ' module-management-editing' : ''}`}>
        <TabPanel header="Module details">
        <div className="h-full" style={{ height: "100%"/*, backgroundColor: 'green'*/ }}>
            <ModuleDetails />
        </div>
        </TabPanel>

        <TabPanel header="User assignments">
        <div className="flex flex-column h-full" style={{ minHeight: 0/*, backgroundColor: 'blue'*/ }}>
            <ModuleAssignments
                saveModuleUsers={saveModuleUsers}
            />
        </div>
        </TabPanel>
        <TabPanel header="Usage & dependencies"><ModuleUsage /></TabPanel>
    </TabView>

    <GeneralErrorDialog
        visible={error.length > 0}
        title={errorTitle}
        message={error}
        details={errorDetails}
        onClose={() => setError('')}
    />
  </>);
}
